// src/view/settings.ts — AYARLAR düğmesi (sağ üst) + paneli: ses seviyesi.
//
// Yerini aldığı şey: tek işi sesi açıp kapatmak olan bir düğme vardı (view/mute.ts). Kullanıcı ses SEVİYESİNİ
// ayarlamak istedi — aç/kapa yetmiyor. Düğme artık bir dişli ve altında bir panel açıyor; ileride başka ayar
// gerekirse (kalite, tuş eşlemesi) yeri hazır.
//
// KORUNAN DAVRANIŞ: `?mute=` zorlaması aynen çalışıyor (otomatik tarayıcı testleri onu kullanıyor ve kaydediyor)
// ve `M` kısayolu hâlâ sesi açıp kapatıyor — düğme kalktı diye kısayolu almak, alışkanlığı kırmak olurdu.
//
// Kalıcılık: seviye `ringout.volume`, sessizlik `ringout.muted`. İkisi AYRI: sessize alıp geri açınca eski
// seviye geri gelsin (kaydırıcıyı sıfıra çekmek ile susturmak farklı niyetler).
// Depolama gizli sekmede İSTİSNA atabilir → her erişim try/catch, hata oyunu durdurmaz.
//
// Tam ekran düğmesiyle (view/fullscreen.ts) aynı köşeyi paylaşır; ikisi de süreçte BİR kez kurulur (main.ts).

/** Ayarların localStorage anahtarları (tek yerde: test de buradan okur). */
export const MUTE_KEY = 'ringout.muted';
export const VOLUME_KEY = 'ringout.volume';

/** Ayarlanabilen en küçük yüzey: Audio sınıfının tamamına bağlanmadan test edilebilsin. */
export interface AudioSettings { setMuted(on: boolean): void; setVolume(v: number): void }

/** 0–1 aralığına kırpar; sayı değilse varsayılana düşer. */
const clamp01 = (v: number, fallback: number): number => (Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : fallback);

function readStore(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }   // gizli sekme / depolama kapalı
}
function writeStore(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* depolama yok: ayar o oturumda yine de geçerli */ }
}

/** Kararı kaydeder; depolama yoksa sessizce geçer. */
export function saveMuted(on: boolean): void { writeStore(MUTE_KEY, on ? '1' : '0'); }
export function saveVolume(v: number): void { writeStore(VOLUME_KEY, String(clamp01(v, 1))); }

/**
 * Açılıştaki ses durumu: `?mute=` varsa O kazanır (ve kaydedilir — testin açtığı sekme yenilense de sessiz
 * kalsın), yoksa son kayıt, o da yoksa sesli. `search` dışarıdan verilebilir (saf test).
 */
export function loadMuted(search: string = typeof location === 'undefined' ? '' : location.search): boolean {
  const q = new URLSearchParams(search).get('mute');
  if (q !== null) {
    const on = q !== '0' && q !== 'false';
    saveMuted(on);
    return on;
  }
  return readStore(MUTE_KEY) === '1';
}

/** Açılıştaki ses seviyesi (0–1). Kayıt yoksa tam ses. Bozuk kayıt oyunu sessiz bırakmasın diye kırpılıyor. */
export function loadVolume(): number {
  const raw = readStore(VOLUME_KEY);
  return raw === null ? 1 : clamp01(Number(raw), 1);
}

const STYLE_ID = 'ro-settings-style';
// Tam ekran düğmesi sağ üstte 34 px + 12 px kenar payı → ayarlar onun SOLUNA (12 + 34 + 8 = 54).
const CSS = `
  #ro-settings { position: fixed; top: 10px; right: 54px; z-index: 41; width: 34px; height: 34px; padding: 0;
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    border: 1px solid rgba(255,255,255,.18); border-radius: 8px; background: rgba(10,13,19,.55); color: #e6ebf3;
    opacity: .45; transition: opacity .15s, background .15s; }
  #ro-settings:hover, #ro-settings:focus-visible, #ro-settings[aria-expanded="true"] { opacity: 1; background: rgba(36,48,73,.9); }
  #ro-settings svg { width: 18px; height: 18px; display: block; }
  #ro-settings-panel { position: fixed; top: 52px; right: 12px; z-index: 41; width: 232px;
    background: rgba(13,17,23,.96); border: 1px solid rgba(255,255,255,.14); border-radius: 10px;
    padding: 12px 14px 14px; color: #e6ebf3; font: 13px/1.45 system-ui, sans-serif;
    box-shadow: 0 8px 28px rgba(0,0,0,.45); }
  #ro-settings-panel[hidden] { display: none; }
  #ro-settings-panel h3 { margin: 0 0 10px; font-size: 12px; letter-spacing: .08em; text-transform: uppercase; opacity: .55; font-weight: 600; }
  #ro-settings-panel .row { display: flex; align-items: center; gap: 8px; }
  #ro-settings-panel .row + .row { margin-top: 10px; }
  #ro-settings-panel .lbl { flex: 1; }
  #ro-settings-panel .val { font-variant-numeric: tabular-nums; opacity: .7; min-width: 34px; text-align: right; }
  #ro-settings-panel input[type=range] { width: 100%; accent-color: #ffd43b; cursor: pointer; }
  /* Sessize alma: kaydırıcıyı sıfıra çekmekten AYRI — geri açınca eski seviye dönsün. */
  #ro-settings-panel .mute { width: 30px; height: 26px; padding: 0; display: flex; align-items: center; justify-content: center;
    border: 1px solid rgba(255,255,255,.18); border-radius: 7px; background: rgba(36,48,73,.5); color: #e6ebf3; cursor: pointer; }
  #ro-settings-panel .mute[aria-pressed="true"] { color: #ff8787; border-color: rgba(255,135,135,.5); }
  #ro-settings-panel .mute svg { width: 15px; height: 15px; display: block; }
  #ro-settings-panel .hint { margin-top: 10px; font-size: 11px; opacity: .45; }
`;

const ICON_GEAR = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>';
// Hoparlör gövdesi ortak; sesli hâlde dalga, sessizde çarpı.
const ICON_ON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16.5 8.5a5 5 0 0 1 0 7"/></svg>';
const ICON_OFF = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M17 9.5l4 5M21 9.5l-4 5"/></svg>';

export interface SettingsHandle {
  dispose(): void;
  /** Sessizliği dışarıdan değiştirir (test / derin bağlantı). */
  setMuted(on: boolean): void;
  /** Seviyeyi dışarıdan değiştirir (0–1). */
  setVolume(v: number): void;
  readonly muted: boolean;
  readonly volume: number;
}

/**
 * Sağ üste AYARLAR düğmesini kurar, `M` kısayolunu bağlar ve açılış durumunu `audio`ya uygular.
 * Süreçte bir kez çağrılır (main.ts).
 */
export function mountSettingsButton(audio: AudioSettings): SettingsHandle {
  let muted = loadMuted();
  let volume = loadVolume();
  audio.setVolume(volume);
  audio.setMuted(muted);

  // DOM'suz ortam (test, headless): durum yine tutulur ve hedefe uygulanır, arayüz kurulmaz.
  if (typeof document === 'undefined') {
    return {
      dispose() { /* yok */ },
      setMuted(on) { muted = on; audio.setMuted(on); saveMuted(on); },
      setVolume(v) { volume = clamp01(v, volume); audio.setVolume(volume); saveVolume(volume); },
      get muted() { return muted; },
      get volume() { return volume; },
    };
  }

  if (!document.getElementById(STYLE_ID)) {
    const st = document.createElement('style');
    st.id = STYLE_ID; st.textContent = CSS;
    document.head.appendChild(st);
  }

  const btn = document.createElement('button');
  btn.id = 'ro-settings';
  btn.type = 'button';
  btn.innerHTML = ICON_GEAR;
  btn.title = 'Ayarlar';
  btn.setAttribute('aria-label', 'Ayarlar');
  btn.setAttribute('aria-expanded', 'false');

  const panel = document.createElement('div');
  panel.id = 'ro-settings-panel';
  panel.hidden = true;
  const title = document.createElement('h3'); title.textContent = 'Ayarlar';
  const row = document.createElement('div'); row.className = 'row';
  const lbl = document.createElement('span'); lbl.className = 'lbl'; lbl.textContent = 'Ses';
  const val = document.createElement('span'); val.className = 'val';
  const muteBtn = document.createElement('button'); muteBtn.className = 'mute'; muteBtn.type = 'button';
  row.append(lbl, val, muteBtn);
  const slider = document.createElement('input');
  slider.type = 'range'; slider.min = '0'; slider.max = '100'; slider.step = '1';
  slider.setAttribute('aria-label', 'Ses seviyesi');
  const sliderRow = document.createElement('div'); sliderRow.className = 'row'; sliderRow.append(slider);
  const hint = document.createElement('div'); hint.className = 'hint'; hint.textContent = 'M ile hızlıca sustur';
  panel.append(title, row, sliderRow, hint);

  document.body.append(btn, panel);

  const sync = (): void => {
    slider.value = String(Math.round(volume * 100));
    val.textContent = muted ? 'kapalı' : `${Math.round(volume * 100)}%`;
    muteBtn.innerHTML = muted ? ICON_OFF : ICON_ON;
    muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
    muteBtn.title = muted ? 'Sesi aç (M)' : 'Sesi kapat (M)';
    muteBtn.setAttribute('aria-label', muteBtn.title);
  };

  const setMuted = (on: boolean): void => { muted = on; audio.setMuted(on); saveMuted(on); sync(); };
  const setVolume = (v: number): void => {
    volume = clamp01(v, volume);
    audio.setVolume(volume);
    saveVolume(volume);
    // Kaydırıcıyı oynatmak sessizliği KALDIRIR: sesi duymak isteyen biri seviyeyi değiştiriyordur.
    if (muted && volume > 0) setMuted(false); else sync();
  };

  const open = (on: boolean): void => {
    panel.hidden = !on;
    btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    // Panel kapanınca odak GERİ ALINIR: kaydırıcıda kalan odak, ok tuşlarını oyundan çalar.
    if (!on) { slider.blur(); muteBtn.blur(); btn.blur(); }
  };

  const onBtn = (e: MouseEvent): void => { e.stopPropagation(); open(panel.hidden); };
  const onMute = (e: MouseEvent): void => { e.stopPropagation(); setMuted(!muted); };
  const onInput = (): void => { setVolume(Number(slider.value) / 100); };
  // Panel dışına tıklama kapatır; içine tıklama kapatmaz.
  const onDocClick = (e: MouseEvent): void => {
    if (panel.hidden) return;
    const t = e.target as Node | null;
    if (t && (panel.contains(t) || btn.contains(t))) return;
    open(false);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && !panel.hidden) { open(false); return; }
    if (e.code !== 'KeyM' || e.ctrlKey || e.altKey || e.metaKey || e.repeat) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
    e.preventDefault();
    setMuted(!muted);
  };

  btn.addEventListener('click', onBtn);
  muteBtn.addEventListener('click', onMute);
  slider.addEventListener('input', onInput);
  document.addEventListener('click', onDocClick);
  addEventListener('keydown', onKey);
  sync();

  return {
    dispose(): void {
      btn.removeEventListener('click', onBtn);
      muteBtn.removeEventListener('click', onMute);
      slider.removeEventListener('input', onInput);
      document.removeEventListener('click', onDocClick);
      removeEventListener('keydown', onKey);
      btn.remove(); panel.remove();
    },
    setMuted,
    setVolume,
    get muted() { return muted; },
    get volume() { return volume; },
  };
}
